import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { CentralMetadata } from 'app/models/central-metadata';
import { ConnectedAppAuth, ConnectedAppEndpoint, ObjectType } from 'app/models/connected-app';
import { FileClass } from 'app/models/file-class';
import { FileClassExternalOperation, FileClassExternalOperationParameter } from 'app/models/file-class-external-operation';
import { CentralMetadataService } from 'app/services/central-metadata.service';
import { EcoDocsTitleService } from 'app/services/ecodocs-title.service';
import { FileClassService } from 'app/services/file-class.service';
import { ConnectedAppAuthorizeComponent } from 'app/views/connected-apps/connected-app-authorize/connected-app-authorize.component';
import { ModalDirective, ToastService } from 'ng-uikit-pro-standard';
import { finalize } from 'rxjs/operators';
import { Form } from '@atefhashem/eforms';

@Component({
  selector: 'app-metadata',
  templateUrl: './metadata.component.html',
  styleUrls: ['./metadata.component.scss']
})
export class MetadataComponent implements OnInit {

  centralMetadata: CentralMetadata[] = [];

  isLoadingFileClasses: boolean = true;
  fileClasses: FileClass[] = [];
  selectedFileClassesTableLimit: number = 5;
  addEditFileClassFormGroup: FormGroup = null;
  isAddingFileClass: boolean = false;
  fileClassToDelete: FileClass = null;
  isDeletingFileClass: boolean = false;

  isLoadingForm: boolean = true;

  addEditExtOpFormGroup: FormGroup = null;
  extOpToEditIndex: number = -1;
  isCheckingExtOpAuth: boolean = false;

  @ViewChild('addEditExtOpModal') addEditExtOpModal: ModalDirective;
  @ViewChild('authorizeApp') authorizeApp: ConnectedAppAuthorizeComponent;
  @ViewChild('addEditFileClassModal') addEditFileClassModal: ModalDirective;
  @ViewChild('deleteFileClassConfirmationModal') deleteFileClassConfirmationModal: ModalDirective;

  constructor(private centralMetadataService: CentralMetadataService, private fileClassService: FileClassService,
              private ecoDocsTitleService: EcoDocsTitleService, private toastService: ToastService,
              private validationHandler: ValidationHandler) { }

  ngOnInit(): void {
    this.ecoDocsTitleService.setTitle('Manage Metadata & File Classes', false);

    document.getElementById('metadata_form').innerHTML = "";
    this.isLoadingForm = true;
    this.centralMetadataService.getForm().pipe(finalize(() => {
      this.isLoadingForm = false;
    })).subscribe((form) => {
      this.centralMetadata = form.central_metadata;

      document.getElementById('metadata_form').innerHTML = "";
      var formioForm = new Form(document.getElementById('metadata_form'), form, { noAlerts: true});
      formioForm.render();
    }, () => {
      this.toastService.error('Error getting metadata!');
    });

    this.refreshFileClasses();
  }

  refreshFileClasses() {
    this.isLoadingFileClasses = true;
    this.fileClassService.list().pipe(finalize(() => {
      this.isLoadingFileClasses = false;
    })).subscribe((f) => {
      this.fileClasses = f;
    });
  }

  innerWidth() {
    return window.innerWidth;
  }

  onAddNewFileClassClick() {
    this.addEditFileClassFormGroup = this.fileClassService.formGroup();
    this.addEditFileClassModal.show();
  }

  onFinishedAddEditFileClass() {
    this.validationHandler.onSubmitCheck(this.addEditFileClassFormGroup);
    if (!this.addEditFileClassFormGroup.valid) {
      return;
    }

    this.isAddingFileClass = true;
    this.fileClassService.put(this.addEditFileClassFormGroup.value).pipe(finalize(()=>{
      this.isAddingFileClass = false;
    })).subscribe(() => {
      this.addEditFileClassModal.hide();
      this.addEditFileClassFormGroup = null;
      this.refreshFileClasses();
    }, (err) => {
      this.validationHandler.serverValidationHandler(err, this.addEditFileClassFormGroup);
    });
  }

  editFileClass(f: FileClass) {
    this.addEditFileClassFormGroup = this.fileClassService.formGroup(f);
    this.addEditFileClassModal.show();
  }

  deleteFileClass() {
    this.isDeletingFileClass = true;
    this.fileClassService.deleteById(this.fileClassToDelete.id).pipe(finalize(() => {
      this.isDeletingFileClass = false;
      this.deleteFileClassConfirmationModal.hide();
      this.refreshFileClasses();
    })).subscribe();
  }

  editExtOpAtIndex(index: number) {
    this.extOpToEditIndex = index;
    this.addEditExtOpFormGroup = this.fileClassService.externalOperationFormGroup(this.addEditFileClassFormGroup.controls['external_operations'].value[index]);
    this.addEditExtOpModal.show();
  }

  deleteExtOpAt(index: number) {
    let extOps: FileClassExternalOperation[] = this.addEditFileClassFormGroup.get('external_operations').value;
    extOps.splice(index, 1);
    this.addEditFileClassFormGroup.get('external_operations').setValue(extOps);
  }

  addExtOp() {
    this.extOpToEditIndex = -1;
    this.addEditExtOpFormGroup = this.fileClassService.externalOperationFormGroup();
    this.addEditExtOpModal.show();
  }

  onFinishedAddEditExtOp() {
    this.validationHandler.onSubmitCheck(this.addEditExtOpFormGroup);
    if (!this.addEditExtOpFormGroup.valid) {
      return;
    }

    if (this.addEditExtOpFormGroup.value.external_operation_common_settings.auth_methods &&
      this.addEditExtOpFormGroup.value.external_operation_common_settings.auth_methods.length > 0 &&
      this.addEditExtOpFormGroup.value.external_operation_common_settings.is_per_user_auth == 0) {
        let connectedAppAuths: ConnectedAppAuth[] = [];
        (<FileClassExternalOperation>this.addEditExtOpFormGroup.value).external_operation_common_settings.auth_methods.forEach((authMethod) => {
          connectedAppAuths.push(authMethod.connected_app_auth);
        });

        this.isCheckingExtOpAuth = true;
        this.authorizeApp.showIfNeedsAuthorization(connectedAppAuths, true).pipe(finalize(() => this.isCheckingExtOpAuth = false)).subscribe((isAllAuthorized) => {
          if (isAllAuthorized) {
            this.onAddEditExtOpAuthorizationComplete();
          }
        });
    } else {
      this.onAddEditExtOpAuthorizationComplete();
    }
  }

  onAddEditExtOpAuthorizationComplete() {
    let externalOperations = <FileClassExternalOperation[]>this.addEditFileClassFormGroup.get('external_operations').value || [];
    if (this.extOpToEditIndex == -1) {
      externalOperations.push(this.addEditExtOpFormGroup.value);
    } else {
      externalOperations[this.extOpToEditIndex] = this.addEditExtOpFormGroup.value;
    }
    this.addEditExtOpModal.hide();
    this.addEditFileClassModal.show();
  }

  onEndpointChanged(event: {connectedAppEndpoint: ConnectedAppEndpoint, changedByUser: boolean}) {
    if (event.changedByUser) {
      setTimeout(() => {
        this.fileClassService.updateExternalOperationParametersFromEndpoint(this.addEditExtOpFormGroup, event.connectedAppEndpoint);      
      }, 1);
    }
  }
  getMatchingMetadataFields(currentParameter: FileClassExternalOperationParameter) {
    let type: ObjectType = (currentParameter.connected_app_parameter.type == ObjectType.ARRAY)? currentParameter.connected_app_parameter.items.type : currentParameter.connected_app_parameter.type;
    let format: string = (currentParameter.connected_app_parameter.type == ObjectType.ARRAY)? currentParameter.connected_app_parameter.items.format : currentParameter.connected_app_parameter.format;
    let isParamRequired: boolean = currentParameter.connected_app_parameter.required;

    let currentFileClass = <FileClass>this.addEditFileClassFormGroup.value;
    
    let allMetadataFields = (currentFileClass.required_metadata || []);
    if (!isParamRequired) {
      allMetadataFields = allMetadataFields.concat(currentFileClass.optional_metadata || []);
    }

    return allMetadataFields.filter((m) => {
      if (type == ObjectType.FILE) {
        return m.type == 'file' || m.type == 'image';
      } else if (type == ObjectType.INTEGER || type == ObjectType.NUMBER) {
        return m.type == 'number' || m.type == 'currency';
      } else if (type == ObjectType.BOOLEAN) {
        return m.type == 'checkbox';
      } else if (type == ObjectType.STRING) {
        if (format) {
          if (format == 'email') {
            return m.type == 'email';
          } else if (format == 'url') {
            return m.type == 'url';
          } else if (format == 'date' || format == 'date-time') {
            return m.type == 'datetime';
          } else {
            return ['textfield', 'textarea', 'number', 'checkbox', 'select', 'datadropdown', 'radio', 'email', 'url', 'phoneNumber', 'datetime', 'currency'].includes(m.type)
          }
        } else {
          return ['textfield', 'textarea', 'number', 'checkbox', 'select', 'datadropdown', 'radio', 'email', 'url', 'phoneNumber', 'datetime', 'currency'].includes(m.type)
        }
      }
    });
  }

  getExtOpBodyModeName() {
    let mode = 'text';
    switch ((<FormGroup>this.addEditExtOpFormGroup.controls['external_operation_common_settings']).controls['accepts'].value) {
      case 'application/javascript':
        mode = 'javascript';
        break;
      case 'application/json':
        mode = 'json';
        break;
      case 'test/html':
        mode = 'html';
        break;
      case 'application/xml':
        mode = 'xml';
        break;
    }

    return mode;
  }

}
